import { Loader2, Search, X } from 'lucide-react'
import { DialogFooter } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { formatClientDisplayName } from '../utils/sessionDetailHelpers'

// Shared UI for the four "copy to another client/program" dialogs: same/different-client
// toggle, searchable client picker with a selected-client chip, target-program select, an
// optional `extra` slot for a dialog-specific secondary selection (meal copy's target meal
// plan), an error banner, and the Cancel/Save footer. Pairs with useClientProgramCopy, which
// owns all the state/fetching this component only renders.
const ClientCopyPicker = ({
  copy,
  renderProgramLabel = (program) => program.name || `Program ${program.id}`,
  extra,
  requireSecondary = false,
  saveLabel = 'Save',
  savingLabel = 'Saving...',
}) => {
  const showProgramSelect = copy.targetType === 'same' || Boolean(copy.selectedClient)
  const saveDisabled =
    copy.saving ||
    (requireSecondary ? !copy.secondarySelectedId : !copy.programId) ||
    (copy.targetType === 'different' && !copy.selectedClient)

  return (
    <>
      <div className="space-y-5">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Copy to</p>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => copy.selectTargetType('same')}
              className={`rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                copy.targetType === 'same'
                  ? 'border-fitness-primary/40 bg-fitness-primary/10 text-fitness-primary'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
              }`}
            >
              Same client
            </button>
            <button
              type="button"
              onClick={() => copy.selectTargetType('different')}
              className={`rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                copy.targetType === 'different'
                  ? 'border-fitness-primary/40 bg-fitness-primary/10 text-fitness-primary'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
              }`}
            >
              Different client
            </button>
          </div>
        </div>

        {copy.targetType === 'different' && (
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Client</label>
            {copy.selectedClient ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-fitness-primary/30 bg-fitness-primary/5 px-3 py-2">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-fitness-primary/15 text-xs font-semibold text-fitness-primary">
                    {formatClientDisplayName(copy.selectedClient).charAt(0).toUpperCase()}
                  </span>
                  <span className="truncate text-sm font-medium text-slate-800">
                    {formatClientDisplayName(copy.selectedClient)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={copy.clearClient}
                  className="flex-shrink-0 rounded-md p-1 text-slate-400 transition hover:bg-white hover:text-slate-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div
                className="relative"
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget)) copy.setClientMenuOpen(false)
                }}
              >
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                {/* Browsers (Chrome especially) ignore autocomplete="off" on fields that look like name/email
                    inputs and paint their own autofill list over our dropdown. So this is a custom combobox
                    that stays read-only until focused - browsers don't offer autofill for read-only inputs. */}
                <input
                  type="text"
                  name="copy-client-search"
                  role="combobox"
                  aria-autocomplete="list"
                  aria-controls="copy-client-options"
                  aria-expanded={copy.clientMenuOpen}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  data-lpignore="true"
                  data-1p-ignore="true"
                  readOnly
                  value={copy.clientSearch}
                  onChange={(e) => {
                    copy.setClientSearch(e.target.value)
                    copy.setClientMenuOpen(true)
                  }}
                  onFocus={(e) => {
                    e.currentTarget.removeAttribute('readonly')
                    copy.setClientMenuOpen(true)
                  }}
                  placeholder={copy.clientsLoading ? 'Loading clients...' : 'Search by name or email'}
                  className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-800 transition focus:border-fitness-primary focus:outline-none focus:ring-2 focus:ring-fitness-primary/20"
                />
                {copy.clientMenuOpen && (
                  <div id="copy-client-options" className="absolute left-0 right-0 z-20 mt-1 max-h-56 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                    {copy.clientsLoading ? (
                      <div className="flex items-center gap-2 px-3 py-3 text-xs text-slate-500">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Loading clients...
                      </div>
                    ) : copy.filteredClients.length ? (
                      copy.filteredClients.map((client) => (
                        <button
                          key={`copy-client-${client.id}`}
                          type="button"
                          onClick={() => copy.selectClient(client)}
                          className="flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 last:border-b-0"
                        >
                          <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-semibold text-slate-500">
                            {formatClientDisplayName(client).charAt(0).toUpperCase()}
                          </span>
                          <span className="truncate">{formatClientDisplayName(client)}</span>
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-3 text-xs text-slate-500">No clients found.</div>
                    )}
                  </div>
                )}
              </div>
            )}
            {copy.clientsError && <p className="text-xs text-red-600">{copy.clientsError}</p>}
          </div>
        )}

        {showProgramSelect && (
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Target program</label>
            <Select
              value={copy.programId}
              onValueChange={(value) => copy.selectProgram(value)}
              disabled={copy.programsLoading || !copy.programs.length}
            >
              <SelectTrigger className="h-10 w-full rounded-lg border-slate-200">
                <SelectValue placeholder={copy.programsLoading ? 'Loading programs...' : 'Select a program'} />
              </SelectTrigger>
              <SelectContent>
                {copy.programs.map((program) => (
                  <SelectItem key={program.id} value={String(program.id)}>
                    {renderProgramLabel(program)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {copy.programsError && <p className="text-sm text-red-600">{copy.programsError}</p>}
            {!copy.programsLoading && !copy.programsError && copy.programs.length === 0 && (
              <p className="text-xs text-slate-500">No programs available for this client.</p>
            )}
          </div>
        )}

        {extra}

        {copy.error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{copy.error}</p>}
      </div>
      <DialogFooter className="sm:justify-end sm:space-x-2">
        <button
          type="button"
          onClick={copy.close}
          disabled={copy.saving}
          className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-70"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={copy.save}
          disabled={saveDisabled}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-fitness-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition hover:bg-fitness-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {copy.saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {savingLabel}
            </>
          ) : (
            saveLabel
          )}
        </button>
      </DialogFooter>
    </>
  )
}

export default ClientCopyPicker
